import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Highlighter, Minus, Plus, RotateCcw } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const EBOOK_BASE = "/assets/ebooks/life-after-21-days";
const pages = [
  { label: "Cover", src: `${EBOOK_BASE}/00-cover.png` },
  ...Array.from({ length: 23 }, (_, i) => ({
    label: `P-${i + 1}`,
    src: `${EBOOK_BASE}/${String(i + 1).padStart(2, "0")}.png`,
  })),
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const StudentEbookReader = () => {
  const { user } = useAuth();
  const [pageIndex, setPageIndex] = useState(0);
  const [zoom, setZoom] = useState(85);
  const [highlightMode, setHighlightMode] = useState(false);
  const storageKey = `kampus-ebook-highlights-${user?.uid || "guest"}`;
  const [highlights, setHighlights] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "{}");
    } catch {
      return {};
    }
  });

  const currentHighlights = useMemo(() => highlights[pageIndex] || [], [highlights, pageIndex]);

  const saveHighlights = (next) => {
    setHighlights(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const goToPage = (next) => setPageIndex(clamp(next, 0, pages.length - 1));
  const changeZoom = (delta) => setZoom((value) => clamp(value + delta, 55, 145));

  const addHighlight = (event) => {
    if (!highlightMode) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const height = pageIndex === 0 ? 8 : 4.8;
    const next = {
      ...highlights,
      [pageIndex]: [
        ...currentHighlights,
        { id: Date.now(), top: clamp(y - height / 2, 2, 96), height },
      ],
    };
    saveHighlights(next);
  };

  const clearPageHighlights = () => {
    const next = { ...highlights, [pageIndex]: [] };
    saveHighlights(next);
  };

  return (
    <div data-testid="student-ebook-reader">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/student/productivity" className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700">
            <ArrowLeft className="h-4 w-4" /> Productivity Tools
          </Link>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">In-app ebook</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">Life After 21 Days</h1>
          <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-slate-500">BY PLANCOURR</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ReaderButton onClick={() => changeZoom(-10)} label="Zoom out"><Minus className="h-4 w-4" /></ReaderButton>
          <span className="min-w-16 rounded-md border border-slate-200 bg-white px-3 py-2 text-center text-sm font-bold text-slate-700">{zoom}%</span>
          <ReaderButton onClick={() => changeZoom(10)} label="Zoom in"><Plus className="h-4 w-4" /></ReaderButton>
          <ReaderButton active={highlightMode} onClick={() => setHighlightMode((v) => !v)} label="Highlight">
            <Highlighter className="h-4 w-4" />
          </ReaderButton>
          <ReaderButton onClick={clearPageHighlights} label="Clear highlights"><RotateCcw className="h-4 w-4" /></ReaderButton>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm font-semibold text-slate-600">
            Page {pageIndex + 1} of {pages.length} · {pages[pageIndex].label}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(pageIndex - 1)}
              disabled={pageIndex === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              type="button"
              onClick={() => goToPage(pageIndex + 1)}
              disabled={pageIndex === pages.length - 1}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-auto rounded-lg bg-slate-100 p-3 md:p-6">
          <div
            className={`relative mx-auto select-none ${highlightMode ? "cursor-crosshair" : ""}`}
            style={{ width: `${zoom}%`, minWidth: "280px", maxWidth: "980px" }}
            onClick={addHighlight}
            onContextMenu={(e) => e.preventDefault()}
          >
            <img
              src={pages[pageIndex].src}
              alt={`Life After 21 Days ${pages[pageIndex].label}`}
              className="pointer-events-none block w-full rounded-md bg-white shadow-lg"
              draggable={false}
            />
            {currentHighlights.map((h) => (
              <div
                key={h.id}
                className="pointer-events-none absolute left-[12%] right-[8%] rounded bg-yellow-300/35 ring-1 ring-yellow-400/20"
                style={{ top: `${h.top}%`, height: `${h.height}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReaderButton = ({ children, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border text-sm font-semibold transition-all ${
      active ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);

export default StudentEbookReader;
